import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import DateFnsUtils from '@date-io/date-fns';
import ExcelExport from './components/ExportExcel';
import { getBillingReportData } from '../../../redux/billing/billingActions';
import { convertFloat } from '../../../redux/utils/customMethods';
import { DB_DATE_FORMAT, DISPLAY_DATE_FORMAT } from '../../../redux/constants';
import { useStyles, StyledTableCell } from '../adminStyles';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import {
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
} from '@material-ui/core';

const BillingReport = () => {
  document.title = 'Sciata Summaries';

  const { gridElRight, tableRoot, tableContainer, btnMobile, inputMobile } = useStyles();

  const dispatch = useDispatch();
  const billingReport = useSelector(state => state.billing.billingReport);
  const [orderAscending, setOrderAscending] = useState(true);
  const [asc, setAsc] = useState('desc');
  const [rows, setRows] = useState([]);
  const [fromDate, setFromDate] = useState(moment().format(DB_DATE_FORMAT));
  const [toDate, setToDate] = useState(moment().format(DB_DATE_FORMAT));
  const [openToDate, setOpenToDate] = useState(false);
  const [dataSet, setDataSet] = useState([]);

  useEffect(() => {
    handleGo();
  }, []);

  const handleGo = () => {
    dispatch(
      getBillingReportData(
        moment(fromDate).format('MM-DD-YYYY'),
        moment(toDate).format('MM-DD-YYYY')
      )
    );
  };

  useEffect(() => {
    let dataSet = [];
    dataSet.push({
      columns: ['Duration', 'From', 'To'],
      data: [
        [
          '',
          moment(fromDate).format(DISPLAY_DATE_FORMAT),
          moment(toDate).format(DISPLAY_DATE_FORMAT),
        ],
      ],
    });

    Object.keys(billingReport || {}).map(proj => {
      let cols = [
        'Project',
        'Name',
        'Employees/ Contractors',
        'Billing Form',
        'Total Hrs',
        'Billing Rate',
        'Cost',
        'Revenue',
        'Labor Exp.',
        'Contractor Exp',
        'Margin %',
      ];
      let recData = [];
      billingReport[proj].map((rec, i) => {
        recData.push([
          proj,
          rec.employeeName,
          rec.employementType === 'contractor' ? 'Contractor' : 'Employee',
          rec.vendorName,
          convertFloat(rec.billingHrs),
          convertFloat(rec.billingRate),
          convertFloat(rec.costToCompany),
          convertFloat(rec.revenue),
          convertFloat(rec.laborExp),
          convertFloat(rec.contractorExp),
          convertFloat(rec.marginPercentage),
        ]);
        return undefined;
      });
      recData.push([
        '',
        '',
        'Total',
        ':',
        convertFloat(totals(billingReport[proj], 'billingHrs').toFixed(2)),
        convertFloat(billingReport[proj][0]['billingRate']),
        convertFloat(billingReport[proj][0]['costToCompany']),
        convertFloat(totals(billingReport[proj], 'revenue').toFixed(2)),
        convertFloat(totals(billingReport[proj], 'laborExp').toFixed(2)),
        convertFloat(totals(billingReport[proj], 'contractorExp').toFixed(2)),
        calcTotalMargin(billingReport[proj]),
      ]);
      dataSet.push({ columns: cols, data: recData });
    });

    setRows(billingReport || {});
    setDataSet(dataSet);
  }, [billingReport]);

  const sortArray = (arr, property) => () => {
    let array = [...arr];
    setOrderAscending(!orderAscending);
    orderAscending ? setAsc('asc') : setAsc('desc');
    if (typeof property === 'string') {
      setRows(
        array.sort((a, b) => {
          let nameA = (a.employeeName || '').toString().toUpperCase();
          let nameB = (b.employeeName || '').toString().toUpperCase();

          if (orderAscending) {
            if (nameA < nameB) {
              return -1;
            }
            if (nameA > nameB) {
              return 1;
            }
            return 0;
          }
          if (!orderAscending) {
            if (nameB < nameA) {
              return -1;
            }
            if (nameB > nameA) {
              return 1;
            }
            return 0;
          }
        })
      );
    }

    property && orderAscending
      ? setRows(array.sort((a, b) => a[property] - b[property]))
      : setRows(array.sort((a, b) => b[property] - a[property]));

    property && !orderAscending
      ? setRows(array.sort((a, b) => b[property] - a[property]))
      : setRows(array.sort((a, b) => a[property] - b[property]));
    return array;
  };

  const totals = (items, key) => {
    return items.map(item => convertFloat(item[key])).reduce((sum, i) => sum + i, 0);
  };

  const calcTotalMargin = r => {
    let res =
      (((totals(r, 'revenue') || 0.0) -
        ((totals(r, 'laborExp') || 0.0) + (totals(r, 'contractorExp') || 0.0))) *
        100) /
      (totals(r, 'revenue') || 0.0);
    return isFinite(res)
      ? res.toFixed(2)
      : (
          (totals(r, 'revenue') || 0.0) -
          ((totals(r, 'laborExp') || 0.0) + (totals(r, 'contractorExp') || 0.0))
        ).toFixed(2);
  };

  return (
    <>
      <h1>Billing Report</h1>
      <Grid container justify='center' alignItems='center'>
        <Grid item xs={3} />
        <Grid item xs={12} md={6} container spacing={1}>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Grid item xs={12} md={6}>
              <KeyboardDatePicker
                variant='inline'
                inputVariant='outlined'
                className={inputMobile}
                format={DISPLAY_DATE_FORMAT.replace('DD', 'dd').replace('YYYY', 'yyyy')}
                margin='normal'
                id='date-picker-inline'
                label='From Date'
                value={moment(fromDate, DB_DATE_FORMAT)}
                autoOk={true}
                onChange={date => {
                  setOpenToDate(true);
                  setFromDate(date);
                }}
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <KeyboardDatePicker
                variant='inline'
                className={inputMobile}
                inputVariant='outlined'
                format={DISPLAY_DATE_FORMAT.replace('DD', 'dd').replace('YYYY', 'yyyy')}
                margin='normal'
                id='date-picker-inline'
                open={openToDate}
                label='To Date'
                value={moment(toDate, DB_DATE_FORMAT)}
                autoOk={true}
                onChange={date => {
                  setOpenToDate(false);
                  setToDate(date);
                }}
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
              />
            </Grid>
          </MuiPickersUtilsProvider>
        </Grid>
        <Grid item xs={12} md={3} className={gridElRight}>
          <Button className={btnMobile} onClick={handleGo} variant='contained'>
            Go
          </Button>
          <ExcelExport exportDataSet={dataSet} />
        </Grid>
      </Grid>
      <Paper className={tableRoot}>
        <TableContainer className={tableContainer}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <StyledTableCell align='center'>Name</StyledTableCell>
                <StyledTableCell align='center'>Employees/ Contractors</StyledTableCell>
                <StyledTableCell align='center'>Billing Form</StyledTableCell>
                <StyledTableCell align='center'>Total Hrs</StyledTableCell>
                <StyledTableCell align='center'>Billing Rate</StyledTableCell>
                <StyledTableCell align='center'>Cost</StyledTableCell>
                <StyledTableCell align='center'>Revenue</StyledTableCell>
                <StyledTableCell align='center'>Labor Exp</StyledTableCell>
                <StyledTableCell align='center'>Contractor Exp</StyledTableCell>
                <StyledTableCell align='center'>Margin %</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.keys(rows).map(row => {
                return (
                  <>
                    <TableRow>
                      <TableCell
                        colSpan='10'
                        style={{
                          backgroundColor: '#c6c6c6',
                        }}
                      >
                        {row}
                      </TableCell>
                    </TableRow>
                    {rows[row].map(r => {
                      return (
                        <TableRow>
                          <TableCell align='center'>{r.employeeName}</TableCell>
                          <TableCell align='center'>
                            {r.employementType === 'contractor'
                              ? 'Contractor'
                              : 'Employee'}
                          </TableCell>
                          <TableCell align='center'>{r.vendorName}</TableCell>
                          <TableCell align='center'>
                            {convertFloat(r.billingHrs).toFixed(2)}
                          </TableCell>
                          <TableCell align='center'>
                            {convertFloat(r.billingRate).toFixed(2)}
                          </TableCell>
                          <TableCell align='center'>
                            {convertFloat(r.costToCompany).toFixed(2)}
                          </TableCell>
                          <TableCell align='center'>
                            {convertFloat(r.revenue).toFixed(2)}
                          </TableCell>
                          <TableCell align='center'>
                            {convertFloat(r.laborExp).toFixed(2)}
                          </TableCell>
                          <TableCell align='center'>
                            {convertFloat(r.contractorExp).toFixed(2)}
                          </TableCell>
                          <TableCell align='center'>
                            {convertFloat(r.marginPercentage).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow>
                      <TableCell align='center'></TableCell>
                      <TableCell align='center'>Total</TableCell>
                      <TableCell align='center'>:</TableCell>
                      <TableCell align='center'>
                        {totals(rows[row], 'billingHrs').toFixed(2)}
                      </TableCell>
                      <TableCell align='center'>
                        {convertFloat(rows[row][0]['billingRate']).toFixed(2)}
                      </TableCell>
                      <TableCell align='center'>
                        {convertFloat(rows[row][0]['costToCompany']).toFixed(2)}
                      </TableCell>
                      <TableCell align='center'>
                        {totals(rows[row], 'revenue').toFixed(2)}
                      </TableCell>
                      <TableCell align='center'>
                        {totals(rows[row], 'laborExp').toFixed(2)}
                      </TableCell>
                      <TableCell align='center'>
                        {totals(rows[row], 'contractorExp').toFixed(2)}
                      </TableCell>
                      <TableCell align='center'>{calcTotalMargin(rows[row])}</TableCell>
                    </TableRow>
                  </>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
};

export default BillingReport;
